// PersonaSelector.js
import React, { useState } from "react";
import { FaUser, FaPlus, FaLinkedin } from "react-icons/fa";
import PersonaModal from "./PersonaModal";

const PersonaSelector = ({
  activeTab,
  setActiveTab,
  availablePersonas,
  selectedPersona,
  onPersonaSelect,
  campaignId,
}) => {
  const [showPersonaModal, setShowPersonaModal] = useState(false);

  const handlePersonaSave = () => {
    // After saving, switch to the select tab to show the newly created persona
    setActiveTab("select");
    setShowPersonaModal(false);
  };

  const renderTabs = () => {
    const tabs = [
      { id: "select", label: "Select from existing" },
      { id: "create", label: "Create new" },
    ];

    return (
      <div className="flex border-b mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`py-2 px-4 ${
              activeTab === tab.id
                ? "border-b-2 border-purple-600 text-purple-600 font-medium"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    );
  };

  const renderSelectTab = () => {
    return (
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availablePersonas.length > 0 ? (
            availablePersonas.map((persona) => (
              <div
                key={persona.id}
                onClick={() => onPersonaSelect(persona.id)}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedPersona && selectedPersona.id === persona.id
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-purple-300 hover:bg-purple-50"
                }`}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div
                      className="h-10 w-10 rounded-full flex items-center justify-center text-white"
                      style={{ backgroundColor: persona.color || "#6D28D9" }}
                    >
                      <FaUser />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {persona.name}
                    </h3>
                    {persona.role && (
                      <p className="mt-1 text-sm text-gray-500">
                        {persona.role}{" "}
                        {persona.company ? `at ${persona.company}` : ""}
                      </p>
                    )}
                    {persona.email && (
                      <p className="mt-1 text-sm text-gray-500">
                        {persona.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center p-8 border border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-500">
                No personas available. Create a new persona to continue.
              </p>
              <button
                onClick={() => setShowPersonaModal(true)}
                className="mt-4 inline-flex items-center text-purple-600 hover:text-purple-800"
              >
                <FaPlus className="mr-1" /> Create Persona
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCreateTab = () => {
    return (
      <div className="text-center p-8">
        <button
          onClick={() => setShowPersonaModal(true)}
          className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 inline-flex items-center"
        >
          <FaPlus className="mr-2" /> Create New Persona
        </button>
        <p className="mt-4 text-gray-500">
          Create a persona to represent yourself or your sales team.
        </p>
      </div>
    );
  };

  const renderSelectedPersonaDetails = () => {
    if (!selectedPersona) return null;

    return (
      <div className="mt-8 border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Selected Persona Details
        </h3>

        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-1">Name</h4>
              <p className="text-gray-900">{selectedPersona.name}</p>
            </div>

            {selectedPersona.company && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">
                  Company
                </h4>
                <p className="text-gray-900">{selectedPersona.company}</p>
              </div>
            )}

            {selectedPersona.email && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">
                  Email
                </h4>
                <p className="text-gray-900">{selectedPersona.email}</p>
              </div>
            )}
          </div>

          {selectedPersona.sellingPoints && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-1">
                Key Selling Points
              </h4>
              <p className="text-gray-900 text-sm whitespace-pre-line">
                {selectedPersona.sellingPoints}
              </p>
            </div>
          )}

          {selectedPersona.signature && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-1">
                Signature
              </h4>
              <div className="border-t border-gray-200 pt-2 text-gray-900 font-medium whitespace-pre-line">
                {selectedPersona.signature}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Choose your persona</h2>

      {renderTabs()}

      {activeTab === "select" ? renderSelectTab() : renderCreateTab()}

      {renderSelectedPersonaDetails()}

      {/* Persona Modal */}
      {showPersonaModal && (
        <PersonaModal
          persona={null}
          onClose={() => setShowPersonaModal(false)}
          onSave={handlePersonaSave}
        />
      )}
    </div>
  );
};

export default PersonaSelector;
